// components/modals/CampaignModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Input, Select, TextArea, ErrorMessage } from '../common';
import { Campaign } from '../../models/Campaign';
import campaignService from '../../services/campaignService';
import { useTranslations } from '../../hooks/useTranslations';
import useTemplatesHook from '../../services/twilio/useTemplatesHook';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import { WhatsAppMessagePreview } from '../WhatsAppMessagePreview';
import { useCustomersPG } from '../../hooks/useCustomersPG';

export function CreateCampaignModal({
  isOpen,
  onClose,
  onSubmit,
  campaign = null,
  mode = 'add'
}) {
  const { templates } = useTemplatesHook();
  const { config } = useBusinessConfig();
  const { searchCustomers } = useCustomersPG();

  // Main form data state
  const [formData, setFormData] = useState({
    name: '',
    targetAudience: '',
    type: 'One-Time',
    templateId: '',
    templateVariables: {},
    sqlQuery: '',
    aiInterpretation: '',
    customers: []
  });
  // Dynamic data state (not stored, fetched fresh each time)
  const [dynamicData, setDynamicData] = useState({
    customers: [],
    customerCount: 0,
    executionTimestamp: null
  });

  // Manual selection state
  const [selectionMode, setSelectionMode] = useState('none'); // 'none', 'ai', 'all', 'manual'
  const [manualSearchTerm, setManualSearchTerm] = useState('');
  const [manualSearchResults, setManualSearchResults] = useState([]);
  const [manualSearchLoading, setManualSearchLoading] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState(new Set());

  const t = useTranslations();
  const approvedTemplates = templates.filter(t => t.status === 'approved');
  const campaignTemplates = approvedTemplates.map(template => ({
    value: template.machineName,
    label: template.displayName
  }));
  // Get cost per message from config, with fallback to 0.20
  const cost = config?.costPerMessage || 0.20
  // Get AI request cost from config, with fallback to 2.00
  const aiRequestCost = config?.costPerAIRequest || 0.05
  const availableCredits = config?.credits || 0
  const hasInsufficientCreditsForAI = aiRequestCost > availableCredits

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInterpreting, setIsInterpreting] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [showCustomers, setShowCustomers] = useState(false);

  // Manual customer search with debounce
  // Only trigger search when the search term actually changes, not when entering manual mode
  useEffect(() => {
    if (selectionMode !== 'manual') return;

    // Only perform search if user has typed something
    // This prevents clearing pre-populated results when entering edit mode
    if (!manualSearchTerm.trim()) {
      return;
    }

    const performSearch = async () => {
      if (manualSearchTerm.trim().length < 3) {
        return;
      }

      setManualSearchLoading(true);
      try {
        const results = await searchCustomers(manualSearchTerm, true);
        // Merge search results with already selected customers to preserve selections
        const selectedCustomersList = manualSearchResults.filter(c => selectedCustomerIds.has(c.id));
        const newResults = results.filter(r => !selectedCustomerIds.has(r.id));
        setManualSearchResults([...selectedCustomersList, ...newResults]);
      } catch (error) {
        console.error('Manual search error:', error);
      } finally {
        setManualSearchLoading(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 500);
    return () => clearTimeout(debounceTimer);
  }, [manualSearchTerm, searchCustomers, selectionMode]);

  const generateTemplatePreview = () => {
    if (!formData.templateId) return '';

    const template = approvedTemplates.find(t => t.machineName === formData.templateId);
    if (!template) return '';

    // Combine header + body + footer
    const headerText = template.headerText ? `*${template.headerText}*\n\n` : '';
    const bodyText = template.textRaw || '';
    const footer = '\n\nהשב "הסר" להסרה';

    let preview = headerText + bodyText + footer;

    // Replace template variables with actual values or placeholders
    let templateVariables = template.variables || [];

    // Handle both array and object formats
    if (!Array.isArray(templateVariables) && typeof templateVariables === 'object') {
      // Convert object to array format for processing
      templateVariables = Object.keys(templateVariables).map(position => ({
        position: parseInt(position),
        name: `משתנה ${position}`,
        placeholder: templateVariables[position]
      }));
    }

    if (Array.isArray(templateVariables) && templateVariables.length > 0) {
      templateVariables.forEach((variable) => {
        const variableValue = formData.templateVariables?.[variable.position] || variable.placeholder || variable.name;
        // Replace {{1}}, {{2}}, etc. with the actual values
        preview = preview.replace(new RegExp(`\\{\\{${variable.position}\\}\\}`, 'g'), variableValue);
      });
    }

    return preview;
  };

  // Reset form when modal opens/closes or campaign changes
  useEffect(() => {
    if (isOpen) {
      if (campaign && mode === 'edit') {
        // Load existing campaign data
        setFormData({
          name: campaign.name || '',
          targetAudience: campaign.targetAudience || '',
          type: campaign.type || 'One-Time',
          templateId: campaign.templateId || '',
          templateVariables: campaign.templateVariables || {},
          sqlQuery: campaign.sqlQuery || '',
          aiInterpretation: campaign.aiInterpretation || '',
          customers: campaign.customers || []
        });

        // Detect campaign type and set appropriate selection mode
        const hasManualSelection = campaign.aiInterpretation && !campaign.sqlQuery && campaign.customers && campaign.customers.length > 0;
        const hasAISelection = campaign.sqlQuery && campaign.aiInterpretation;
        const hasAllCustomers = campaign.sqlQuery === 'SELECT * FROM customers';

        if (hasManualSelection) {
          // Manual selection campaign - show stored customers
          setSelectionMode('manual-confirmed');
          setShowInterpretation(true);
          setDynamicData({
            customers: campaign.customers,
            customerCount: campaign.customers.length,
            executionTimestamp: Date.now()
          });
          setShowCustomers(true);
          // Prepare for potential editing
          setSelectedCustomerIds(new Set(campaign.customers.map(c => c.id)));
          setManualSearchResults([]);
          setManualSearchTerm('');
        } else if (hasAllCustomers) {
          // All customers selection
          setSelectionMode('all');
          setShowInterpretation(true);
          setDynamicData({
            customers: [],
            customerCount: 0,
            executionTimestamp: null
          });
          setShowCustomers(false);
          setManualSearchTerm('');
          setManualSearchResults([]);
          setSelectedCustomerIds(new Set());
        } else if (hasAISelection) {
          // AI-based selection
          setSelectionMode('ai');
          setShowInterpretation(true);
          setDynamicData({
            customers: [],
            customerCount: 0,
            executionTimestamp: null
          });
          setShowCustomers(false);
          setManualSearchTerm('');
          setManualSearchResults([]);
          setSelectedCustomerIds(new Set());
        } else {
          // No selection yet
          setSelectionMode('none');
          setShowInterpretation(false);
          setDynamicData({
            customers: [],
            customerCount: 0,
            executionTimestamp: null
          });
          setShowCustomers(false);
          setManualSearchTerm('');
          setManualSearchResults([]);
          setSelectedCustomerIds(new Set());
        }

      } else {
        // Reset form for new campaign
        setFormData({
          name: '',
          targetAudience: '',
          type: 'One-Time',
          templateId: '',
          templateVariables: {},
          sqlQuery: '',
          aiInterpretation: '',
          customers: []
        });

        setDynamicData({
          customers: [],
          customerCount: 0,
          executionTimestamp: null
        });

        setShowInterpretation(false);
        setShowCustomers(false);
        setSelectionMode('none');
        setManualSearchTerm('');
        setManualSearchResults([]);
        setSelectedCustomerIds(new Set());
      }

      // Reset UI state
      setErrors({});
      setIsInterpreting(false);
      setIsLoadingCustomers(false);
    }
  }, [isOpen, campaign, mode]);

  // Handle manual customer selection mode
  const handleEnterManualSelection = () => {
    setSelectionMode('manual');
    setManualSearchTerm('');
    setManualSearchResults([]);
    setSelectedCustomerIds(new Set());

    // Clear AI-based data
    setFormData(prev => ({
      ...prev,
      sqlQuery: '',
      aiInterpretation: '',
      customers: [],
      targetAudience: ''
    }));
    setDynamicData({
      customers: [],
      customerCount: 0,
      executionTimestamp: null
    });
    setShowInterpretation(false);
    setShowCustomers(false);
  };

  const handleToggleCustomerSelection = (customer) => {
    setSelectedCustomerIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customer.id)) {
        newSet.delete(customer.id);
      } else {
        newSet.add(customer.id);
      }
      return newSet;
    });
  };

  const handleConfirmManualSelection = () => {
    // Get the full customer objects for selected IDs
    const selectedCustomers = manualSearchResults.filter(c => selectedCustomerIds.has(c.id));
    const optimizedCustomers = optimizeCustomersForStorage(selectedCustomers);

    // For manual selection, we don't use an SQL query - we store the customers directly
    // We use an empty sqlQuery to indicate manual selection
    setFormData(prev => ({
      ...prev,
      sqlQuery: '', // Empty query for manual selection
      aiInterpretation: `בחירה ידנית של ${selectedCustomers.length} לקוחות`,
      customers: optimizedCustomers
    }));

    setDynamicData({
      customers: selectedCustomers,
      customerCount: selectedCustomers.length,
      executionTimestamp: Date.now()
    });

    setShowInterpretation(true);
    setShowCustomers(true);
    setSelectionMode('manual-confirmed');
  };

  // Handle AI audience interpretation using real Firebase functions
  const handleInterpretAudience = async () => {
    if (!formData.targetAudience.trim()) {
      setErrors(prev => ({
        ...prev,
        targetAudience: 'Please enter a target audience description first'
      }));
      return;
    }

    // Validate description
    const validation = campaignService.validateTargetAudienceDescription(formData.targetAudience);
    if (!validation.isValid) {
      setErrors(prev => ({
        ...prev,
        targetAudience: validation.errors[0]
      }));
      return;
    }

    setIsInterpreting(true);
    setErrors(prev => ({ ...prev, targetAudience: undefined }));

    try {
      console.log('Starting AI interpretation for:', formData.targetAudience);

      // Call the real Firebase function that returns both interpretation and execution
      const result = await campaignService.interpretAndExecuteTargetAudience(formData.targetAudience);

      console.log('AI interpretation result:', result);

      if (!result.success) {
        throw new Error(result.error || t.failedToInterpretAudience);
      }

      const optimizedCustomers = optimizeCustomersForStorage(result.customers);
      // Update form data with persistent AI fields AND optimized customers
      setFormData(prev => ({
        ...prev,
        sqlQuery: result.sqlQuery,
        aiInterpretation: result.aiInterpretation,
        customers: optimizedCustomers || []
      }));

      // Update dynamic data with fresh customer results
      setDynamicData({
        customers: result.customers,
        customerCount: result.customerCount,
        executionTimestamp: result.timestamp
      });

      setSelectionMode('ai');
      setShowInterpretation(true);

      // If we got customers, also show them
      if (result.customers && result.customers.length > 0) {
        setShowCustomers(true);
      }

    } catch (error) {
      console.error('AI interpretation error:', error);
      setErrors(prev => ({
        ...prev,
        targetAudience: error.message || t.failedToInterpretAudience
      }));
    } finally {
      setIsInterpreting(false);
    }
  };

  const optimizeCustomersForStorage = (customers) => {
    if (!Array.isArray(customers)) return [];

    return customers.map(customer => ({
      id: customer.id,
      full_name: customer.full_name || customer.customerName || 'לקוח יקר',
      phone: customer.phone
    })).filter(customer => customer.id && customer.phone);
  };

  // Handle loading fresh customers using stored SQL query
  const handleLoadCustomers = async () => {
    // For manual selection, customers are already loaded and stored
    if (selectionMode === 'manual-confirmed' || !formData.sqlQuery) {
      // Use already stored customers
      if (formData.customers && formData.customers.length > 0) {
        console.log('Using stored customers for manual selection');
        setDynamicData({
          customers: formData.customers,
          customerCount: formData.customers.length,
          executionTimestamp: Date.now()
        });
        setShowCustomers(true);
        return;
      }

      setErrors(prev => ({
        ...prev,
        general: 'No customers available. Please select customers first.'
      }));
      return;
    }

    setIsLoadingCustomers(true);
    setErrors(prev => ({ ...prev, general: undefined }));

    try {
      console.log('Executing stored query:', formData.sqlQuery);

      // Execute the stored SQL query to get fresh customer data
      const result = await campaignService.executeStoredQuery(formData.sqlQuery);

      console.log('Query execution result:', result);

      if (!result.success) {
        throw new Error(result.error || t.failedToLoadCustomers);
      }

      // Update dynamic data with fresh results
      setDynamicData({
        customers: result.customers,
        customerCount: result.customerCount,
        executionTimestamp: result.executedAt
      });

      setShowCustomers(true);

    } catch (error) {
      console.error('Customer loading error:', error);
      setErrors(prev => ({
        ...prev,
        general: error.message || t.failedToLoadCustomers
      }));
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Handle getting all customers without AI interpretation
  const handleGetAllCustomers = async () => {
    setIsLoadingCustomers(true);
    setErrors(prev => ({ ...prev, targetAudience: undefined }));

    try {
      // Simple SELECT * query to get all customers
      const allCustomersQuery = 'SELECT * FROM customers';

      console.log('Executing query for all customers');

      const result = await campaignService.executeStoredQuery(allCustomersQuery);

      console.log('All customers result:', result);

      if (!result.success) {
        throw new Error(result.error || t.failedToLoadCustomers);
      }

      const optimizedCustomers = optimizeCustomersForStorage(result.customers);

      // Update form data with the hardcoded query and customers
      setFormData(prev => ({
        ...prev,
        sqlQuery: allCustomersQuery,
        aiInterpretation: 'כל הלקוחות במערכת',
        customers: optimizedCustomers || []
      }));

      // Update dynamic data with results
      setDynamicData({
        customers: result.customers,
        customerCount: result.customerCount,
        executionTimestamp: Date.now()
      });

      setSelectionMode('all');
      setShowInterpretation(true);
      setShowCustomers(true);

    } catch (error) {
      console.error('Get all customers error:', error);
      setErrors(prev => ({
        ...prev,
        targetAudience: error.message || t.failedToLoadCustomers
      }));
    } finally {
      setIsLoadingCustomers(false);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }

    // If user changes target audience, reset AI interpretation
    if (name === 'targetAudience' && formData.sqlQuery) {
      setFormData(prev => ({
        ...prev,
        sqlQuery: '',
        aiInterpretation: '',
        customers: []
      }));
      setShowInterpretation(false);
      setShowCustomers(false);
      setDynamicData({
        customers: [],
        customerCount: 0,
        executionTimestamp: null
      });
      setSelectionMode('none');
      setSelectedCustomerIds(new Set());
    }
  };

  // Validate form
  const validateForm = () => {
    const validation = Campaign.validate(formData);
    if (!validation.isValid) {
      const newErrors = {};
      validation.errors.forEach(error => {
        if (error.includes('name')) newErrors.name = error;
        else if (error.includes('target audience')) newErrors.targetAudience = error;
        else if (error.includes('type')) newErrors.type = error;
        else if (error.includes('message') || error.includes('content')) newErrors.messageContent = error;
        else newErrors.general = error;
      });
      setErrors(newErrors);
      return false;
    }

    // Validate template variables if template is selected
    if (formData.templateId) {
      const selectedTemplate = approvedTemplates.find(t => t.machineName === formData.templateId);
      let templateVariables = selectedTemplate?.variables || [];

      // Handle both array and object formats
      if (!Array.isArray(templateVariables) && typeof templateVariables === 'object') {
        templateVariables = Object.keys(templateVariables).map(position => ({
          position: parseInt(position),
          name: `משתנה ${position}`,
          placeholder: templateVariables[position]
        }));
      }

      // Filter out automatic variables (שם לקוח is auto-filled)
      const manualVariables = Array.isArray(templateVariables)
        ? templateVariables.filter(v => v.name !== 'שם לקוח' && v.position !== 1)
        : [];

      if (manualVariables.length > 0) {
        const variableErrors = {};
        let hasVariableError = false;

        manualVariables.forEach(variable => {
          const value = formData.templateVariables?.[variable.position];
          if (!value || !value.trim()) {
            variableErrors[variable.position] = `יש למלא את השדה: ${variable.name}`;
            hasVariableError = true;
          }
        });

        if (hasVariableError) {
          setErrors({ ...errors, templateVariables: variableErrors });
          return false;
        }
      }
    }

    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await onSubmit(formData);

      if (result.success) {
        onClose();
      } else {
        setErrors({ general: result.error || 'Failed to save campaign' });
      }
    } catch (error) {
      setErrors({ general: error.message || 'An unexpected error occurred' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper functions
  const hasAITargeting = () => {
    // Returns true for AI selection (has sqlQuery and aiInterpretation)
    // OR for manual selection (has aiInterpretation and customers but no sqlQuery)
    const hasAISelection = !!(formData.sqlQuery && formData.aiInterpretation);
    const hasManualSelection = !!(formData.aiInterpretation && !formData.sqlQuery && formData.customers && formData.customers.length > 0);
    return hasAISelection || hasManualSelection;
  };

  const hasCustomerData = () => {
    return dynamicData.customers && dynamicData.customers.length > 0;
  };

  const getCustomerCountDisplay = () => {
    if (dynamicData.customerCount === 0) return t.noCustomersFound;
    if (dynamicData.customerCount === 1) return t.oneCustomerFound;
    return `${dynamicData.customerCount} ${t.customersFound}`;
  };

  const formatCustomerForDisplay = (customer) => {
    return {
      id: customer.id,
      name: customer.full_name,
      phone: customer.phone,
      email: customer.email,
      skinType: customer.skin_type,
      preferredContact: customer.preferred_contact,
      totalSpent: parseFloat(customer.total_spent || 0),
      totalAppointments: parseInt(customer.total_appointments || 0),
      lastAppointmentDate: customer.last_appointment_date,
      daysSinceLastAppointment: parseInt(customer.days_since_last_appointment || 0),
      formattedTotalSpent: new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency: 'ILS'
      }).format(parseFloat(customer.total_spent || 0)),
      formattedLastAppointment: customer.last_appointment_date
        ? new Date(customer.last_appointment_date).toLocaleDateString()
        : 'Never'
    };
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
            </svg>
          </div>
          <span>{mode === 'edit' ? t.editCampaign : t.newCampaign}</span>
        </div>
      }
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General Error */}
        {errors.general && (
          <ErrorMessage message={errors.general} />
        )}

        {/* Campaign Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t.campaignName} *
          </label>
          <Input
            id="name"
            name="name"
            type="text"
            value={formData.name}
            onChange={handleInputChange}
            placeholder="e.g., Summer Glow Promo"
            className={errors.name ? 'border-red-300' : ''}
            required
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        {/* Target Audience */}
        <div>
          <label htmlFor="targetAudience" className="block text-sm font-medium text-gray-700 mb-1">
            {t.targetAudience}
          </label>
          <TextArea
            id="targetAudience"
            name="targetAudience"
            value={formData.targetAudience}
            onChange={handleInputChange}
            placeholder="תאר את קהל היעד (למשל: לקוחות שלא קבעו תור ב-3 החודשים האחרונים) או השתמש בכפתור 'כל הלקוחות'"
            rows={5}
            className={errors.targetAudience ? 'border-red-300' : ''}
          />
          {errors.targetAudience && <p className="mt-1 text-sm text-red-600">{errors.targetAudience}</p>}

          {/* Action Buttons */}
          <div className="mt-2 flex items-start gap-3 flex-wrap">
            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleInterpretAudience}
                disabled={isInterpreting || !formData.targetAudience.trim() || hasInsufficientCreditsForAI || selectionMode === 'manual'}
                className="min-w-[90px] bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white border-none disabled:from-gray-300 disabled:to-gray-400"
                title={hasInsufficientCreditsForAI ? `אין מספיק קרדיט. נדרש: ${aiRequestCost.toFixed(2)}₪` : ''}
              >
                {isInterpreting ? (
                  <div className="flex items-center space-x-1">
                    <svg className="animate-spin h-4 w-4 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-xs font-medium">AI</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-xs font-medium">{t.interpretAudience}</span>
                  </div>
                )}
              </Button>
              {hasInsufficientCreditsForAI && (
                <span className="text-xs text-red-600 px-1">
                  אין מספיק קרדיט
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleGetAllCustomers}
                disabled={isLoadingCustomers || selectionMode === 'manual'}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-none disabled:from-gray-300 disabled:to-gray-400"
                title="קבל את כל הלקוחות ללא שימוש ב-AI"
              >
                {isLoadingCustomers ? (
                  <div className="flex items-center space-x-1">
                    <svg className="animate-spin h-4 w-4 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-xs font-medium">טוען...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1">
                    <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13.5 8.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM17 10.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                    </svg>
                    <span className="text-xs font-medium">כל הלקוחות</span>
                  </div>
                )}
              </Button>
            </div>

            <div className="flex flex-col gap-1">
              <Button
                type="button"
                variant="secondary"
                size="small"
                onClick={handleEnterManualSelection}
                disabled={selectionMode === 'manual'}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-none disabled:from-gray-300 disabled:to-gray-400"
                title="בחר לקוחות באופן ידני"
              >
                <div className="flex items-center space-x-1">
                  <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  <span className="text-xs font-medium">בחירה ידנית</span>
                </div>
              </Button>
            </div>
          </div>

          {/* Manual Selection Interface */}
          {selectionMode === 'manual' && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <h4 className="text-sm font-medium text-green-800 mb-3">בחירה ידנית של לקוחות</h4>

              {/* Search Input */}
              <div className="mb-3">
                <Input
                  placeholder="חפש לקוחות לפי שם, טלפון או אימייל (לפחות 3 תווים)"
                  value={manualSearchTerm}
                  onChange={(e) => setManualSearchTerm(e.target.value)}
                  className="border-green-300 focus:border-green-500 focus:ring-green-500"
                />
                {manualSearchLoading && (
                  <div className="mt-2 flex items-center space-x-2 text-sm text-green-700">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>מחפש...</span>
                  </div>
                )}
              </div>

              {/* Search Results */}
              {manualSearchResults.length > 0 && (
                <div className="mb-3">
                  <p className="text-sm text-green-700 mb-2">
                    נמצאו {manualSearchResults.length} לקוחות - בחר את הלקוחות הרצויים:
                  </p>
                  <div className="max-h-60 overflow-y-auto space-y-2 bg-white p-3 rounded border border-green-200">
                    {manualSearchResults.map((customer) => (
                      <div
                        key={customer.id}
                        className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-colors ${
                          selectedCustomerIds.has(customer.id)
                            ? 'bg-green-100 border-green-400'
                            : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                        }`}
                        onClick={() => handleToggleCustomerSelection(customer)}
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={selectedCustomerIds.has(customer.id)}
                              onChange={() => handleToggleCustomerSelection(customer)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded ml-2"
                              onClick={(e) => e.stopPropagation()}
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{customer.full_name}</p>
                              <p className="text-xs text-gray-500">{customer.phone}</p>
                            </div>
                          </div>
                        </div>
                        {customer.email && (
                          <p className="text-xs text-gray-500 hidden sm:block">{customer.email}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selected Count and Confirm */}
              {selectedCustomerIds.size > 0 && (
                <div className="flex items-center justify-between mt-3 p-3 bg-white rounded border border-green-300">
                  <span className="text-sm font-medium text-green-800">
                    נבחרו {selectedCustomerIds.size} לקוחות
                  </span>
                  <Button
                    type="button"
                    size="small"
                    onClick={handleConfirmManualSelection}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    אשר בחירה
                  </Button>
                </div>
              )}

              {/* Help Text */}
              {manualSearchTerm.length > 0 && manualSearchTerm.length < 3 && (
                <p className="text-xs text-gray-500 mt-2">
                  הזן לפחות 3 תווים כדי להתחיל לחפש
                </p>
              )}
              {manualSearchTerm.length >= 3 && !manualSearchLoading && manualSearchResults.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  לא נמצאו לקוחות התואמים את החיפוש
                </p>
              )}
            </div>
          )}

          {/* AI Interpretation Loading State */}
          {isInterpreting && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4 ml-1 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm text-blue-800">
                  {t.aiAnalyzingTargetAudience}
                </span>
              </div>
            </div>
          )}

          {/* AI Interpretation Results */}
          {showInterpretation && hasAITargeting() && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-600 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-green-800 mb-1">{t.aiInterpretation}</h4>
                  <p className="text-sm text-green-700 mb-2">{formData.aiInterpretation}</p>

                  {/* View Customers Button (only for AI/All modes) */}
                  {selectionMode !== 'manual-confirmed' && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={handleLoadCustomers}
                      disabled={isLoadingCustomers}
                      className="bg-green-600 hover:bg-green-700 text-white border-none"
                    >
                      {isLoadingCustomers ? (
                        <div className="flex items-center space-x-2">
                          <svg className="animate-spin h-4 w-4 ml-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>{t.loading}</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1a1.5 1.5 0 01-3 0V5.697a1.5 1.5 0 013 0V13.5zM21 14.25c0 .414-.336.75-.75.75h-14.5A.75.75 0 015 14.25v-8.5c0-.414.336-.75.75-.75h14.5c.414 0 .75.336.75.75v8.5z" />
                          </svg>
                          <span>{t.view}-{hasCustomerData() ? dynamicData.customerCount : ''} {t.matchingAudience}</span>
                        </div>
                      )}
                    </Button>
                  )}

                  {/* Edit Manual Selection Button */}
                  {selectionMode === 'manual-confirmed' && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="small"
                      onClick={() => {
                        // Re-populate the selected customer IDs from stored customers
                        const customerIds = new Set(formData.customers.map(c => c.id));
                        setSelectedCustomerIds(customerIds);
                        setManualSearchResults(dynamicData.customers);
                        setSelectionMode('manual');
                      }}
                      className="bg-green-600 hover:bg-green-700 text-white border-none"
                    >
                      <div className="flex items-center space-x-2">
                        <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        <span>ערוך בחירה</span>
                      </div>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Customer List */}
          {showCustomers && hasCustomerData() && (
            <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-md">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-medium text-gray-800">{getCustomerCountDisplay()}</h4>
                <span className="text-xs text-gray-500">
                  {t.updated} {new Date(dynamicData.executionTimestamp).toLocaleTimeString()}
                </span>
              </div>

              <div className="max-h-60 overflow-y-auto space-y-2">
                {dynamicData.customers.map((customer) => {
                  const formatted = formatCustomerForDisplay(customer);
                  return (
                    <div key={formatted.id} className="flex items-center justify-between p-3 bg-white rounded border border-gray-200">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{formatted.name}</p>
                            <p className="text-xs text-gray-500">{formatted.phone}</p>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">{formatted.formattedTotalSpent}</p>
                        <p className="text-xs text-gray-500">
                          {t.lastVisit}: {formatted.formattedLastAppointment}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        { /* Message Template */}
        <div>
          <label htmlFor="templateId" className="block text-sm font-medium text-gray-700 mb-1">
            Message Template *
          </label>
          <Select
            id="templateId"
            name="templateId"
            value={formData.templateId}
            onChange={handleInputChange}
            options={campaignTemplates}
            placeholder="Select a message template"
            className={errors.templateId ? 'border-red-300' : ''}
            required
          />
          {errors.templateId && <p className="mt-1 text-sm text-red-600">{errors.templateId}</p>}
        </div>

        {/* Template Variables */}
        {formData.templateId && (() => {
          const selectedTemplate = approvedTemplates.find(t => t.machineName === formData.templateId);
          let templateVariables = selectedTemplate?.variables || [];

          // Handle both array and object formats
          // Array format: [{position: 1, name: 'x', placeholder: 'y'}, ...]
          // Object format: {1: 'value1', 2: 'value2', ...}
          if (!Array.isArray(templateVariables) && typeof templateVariables === 'object') {
            // Convert object to array format
            templateVariables = Object.keys(templateVariables).map(position => ({
              position: parseInt(position),
              name: `משתנה ${position}`,
              placeholder: templateVariables[position]
            }));
          }

          // Filter out automatic variables (שם לקוח is position 1 and is auto-filled from customer data)
          const manualVariables = Array.isArray(templateVariables)
            ? templateVariables.filter(v => v.name !== 'שם לקוח' && v.position !== 1)
            : [];

          if (manualVariables.length > 0) {
            return (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-gray-700">ערכי משתנים</h3>
                <p className="text-xs text-gray-500">
                  הזן ערכים למשתנים בתבנית. ערכים אלה ישמשו עבור כל הלקוחות בקמפיין. (שם הלקוח ימולא אוטומטית)
                </p>
                {manualVariables.map((variable) => (
                  <div key={variable.position}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {variable.name}
                    </label>
                    <Input
                      type="text"
                      value={formData.templateVariables?.[variable.position] || ''}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          templateVariables: {
                            ...prev.templateVariables,
                            [variable.position]: e.target.value
                          }
                        }));
                      }}
                      placeholder={variable.placeholder || variable.name}
                      className={errors.templateVariables?.[variable.position] ? 'border-red-300' : ''}
                    />
                    {errors.templateVariables?.[variable.position] && (
                      <p className="mt-1 text-sm text-red-600">{errors.templateVariables[variable.position]}</p>
                    )}
                  </div>
                ))}
              </div>
            );
          }
          return null;
        })()}

        {/* Template Preview */}
        {formData.templateId && (() => {
          const template = approvedTemplates.find(t => t.machineName === formData.templateId);
          if (!template) return null;

          return (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                תצוגה מקדימה של ההודעה
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <WhatsAppMessagePreview
                  headerType={template.headerType || 'text'}
                  headerText={template.headerText}
                  headerMediaUrl={template.headerMediaUrl}
                  text={generateTemplatePreview()}
                  buttons={template.buttons || []}
                  showContainer={false}
                  alignment="right"
                  className="p-4"
                />
                <p className="text-xs text-gray-500 px-4 pb-3">
                  זוהי תבנית הודעת ווטסאפ מאושרת מראש. ערכים שמופיעים בסוגריים מסולסלים יוחלפו אוטומטית בעת הפצת ההודעה.
                </p>
              </div>
            </div>
          );
        })()}

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t.cancel}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t.saving}</span>
              </div>
            ) : (
              mode === 'edit' ? t.updateCampaign : t.createCampaign
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
}